import { computed, Injectable, linkedSignal, signal } from "@angular/core";
import { s25Resource } from "../../util/s25.resource";
import { EventService } from "../../services/event.service";
import { S25Const } from "../../util/s25-const";
import { S25Assert } from "../../util/s25.assert";
import { S25Util } from "../../util/s25-util";
import {
    SocChatExternalType,
    SocChannelType,
    SocContactUser,
    SocPublicChannelResult,
    StandoutClassroomService,
} from "../../services/standout.classroom.service";
import {
    buildEventChatChannelTitle,
    buildEventChatExternalId,
    EventChatType,
} from "../../services/soc.chat.channel.util";
import { ContactService } from "../../services/contact.service";
import { NotificationService } from "../../services/notification.service";
import { TelemetryService } from "../../services/telemetry.service";

export type { EventChatType } from "../../services/soc.chat.channel.util";

export interface EventAssignedRoleContact {
    contactId: number;
    name: string;
    email: string;
    roleLabels: string[];
}

interface ContactWsAddress {
    email?: string;
}

interface ContactWsNode {
    contact_id: number | string;
    first_name?: string;
    last_name?: string;
    address?: ContactWsAddress | ContactWsAddress[];
}

@Injectable()
export class EventChatStore {
    private readonly _eventId = signal<number | string | undefined>(undefined);
    readonly eventId = this._eventId.asReadonly();

    readonly eventResource = s25Resource({
        if: () => this._eventId() != null,
        loader: () => EventService.getEventById(this._eventId()!),
    });
    readonly eventTitle = computed(() => this.eventResource.value()?.event_name ?? "");
    readonly hasInstructor = computed(() => !!this.getContactForRole(S25Const.instructorRole.event));
    /** Every contact assigned to an event role (for "Chat with Everyone"), deduped by contact_id. */
    readonly everyoneRoleContacts = computed(() => this.extractAllRoleContacts());

    readonly chatLoading = signal(false);
    readonly activeChatType = signal<EventChatType | undefined>(undefined);
    readonly chatActive = computed(() => this.activeChatType() != null);
    readonly chatMinimized = linkedSignal<EventChatType | undefined, boolean>({
        source: this.activeChatType,
        computation: () => false,
    });
    readonly channelId = signal<string | null>(null);
    readonly channelTitle = signal("");
    readonly channelType = signal<SocChannelType>("private");
    readonly socChannelId = signal<number | undefined>(undefined);
    readonly channelKey = computed(() => {
        if (this.channelType() === "direct" && this.socChannelId() != null) {
            return String(this.socChannelId());
        }
        return this.channelId() ?? (this._eventId() != null ? String(this._eventId()) : "");
    });

    readonly chatTitle = computed((): string => {
        const chatType = this.activeChatType();
        switch (chatType) {
            case "all":
                return "Chat with Everyone";
            case "scheduler":
                return "Chat with Scheduler";
            case "requestor":
                return "Chat with Requestor";
            case "instructor":
                return "Chat with Instructor";
            case undefined:
                return "";
            default:
                return S25Assert.never(chatType, "Unknown chat type");
        }
    });

    setEventId(id: number | string) {
        this._eventId.set(id);
    }

    closeChat() {
        this.activeChatType.set(undefined);
        this.channelId.set(null);
        this.channelTitle.set("");
        this.channelType.set("private");
        this.socChannelId.set(undefined);
    }

    async startChat(chatType: EventChatType) {
        TelemetryService.send("Chat", "Chat_Event");

        const [tokenHolder] = await S25Util.Maybe(StandoutClassroomService.getAuthToken());
        if (!tokenHolder) {
            console.error("Chat: failed to obtain auth token");
            S25Util.showError(
                "Chat is unavailable. Your account may be missing required information. Please contact your administrator.",
            );
            return;
        }

        this.chatLoading.set(true);
        this.activeChatType.set(chatType);
        try {
            const users = await this.resolveUsers(chatType);
            if (!users.length) throw new Error("No users with 25Live accounts found");

            const eventId = this._eventId();
            if (eventId == null) throw new Error("Event id is not set");

            const eventLocator = chatType === "all" ? await EventService.getEventLocator(Number(eventId)) : undefined;
            const title = buildEventChatChannelTitle(this.eventTitle(), chatType, eventLocator ?? undefined);
            const initiatingContId = chatType !== "all" ? await ContactService.getCurrentId() : undefined;
            const externalId = buildEventChatExternalId(eventId, chatType, initiatingContId);

            const result = await StandoutClassroomService.createPrivateChannelFromUsers(
                users,
                externalId,
                SocChatExternalType.Event,
                title,
            );

            this.notifySkippedUsers(result);
            this.channelId.set(result.externalId);
            this.channelTitle.set(title);
            this.channelType.set(result.channelType);
            this.socChannelId.set(result.socChannelId);
            void StandoutClassroomService.forceRefreshUnreadChats();
        } catch (err) {
            this.activeChatType.set(undefined);
            const message = err instanceof Error ? err.message : "Unable to open chat. Please try again.";
            NotificationService.post(message);
        } finally {
            this.chatLoading.set(false);
        }
    }

    private async resolveUsers(chatType: EventChatType): Promise<SocContactUser[]> {
        switch (chatType) {
            case "all":
                return this.resolveContacts(
                    this.everyoneRoleContacts().map((assigned) => ({
                        contactId: assigned.contactId,
                        name: assigned.name,
                        email: assigned.email,
                    })),
                );
            case "scheduler": {
                const contact = this.getContactForRole(S25Const.schedulerRole.event);
                return contact ? this.resolveContacts([contact]) : [];
            }
            case "requestor": {
                const contact = this.getContactForRole(S25Const.requestorRole.event);
                return contact ? this.resolveContacts([contact]) : [];
            }
            case "instructor": {
                const contact = this.getContactForRole(S25Const.instructorRole.event);
                return contact ? this.resolveContacts([contact]) : [];
            }
            default:
                return S25Assert.never(chatType, "Unknown chat type");
        }
    }

    private extractAllRoleContacts(): EventAssignedRoleContact[] {
        const roleArray: any[] = S25Util.array.forceArray(this.eventResource.value()?.role ?? []);
        const byContactId = new Map<number, EventAssignedRoleContact>();
        for (const roleRow of roleArray) {
            if (!roleRow?.contact?.contact_id) continue;
            const contactId = parseInt(roleRow.contact.contact_id, 10);
            if (!contactId) continue;
            const roleLabel = roleRow.role_name ?? String(roleRow.role_id ?? "");
            const existing = byContactId.get(contactId);
            if (existing) {
                existing.roleLabels.push(roleLabel);
            } else {
                byContactId.set(contactId, {
                    contactId,
                    name: roleRow.contact.contact_name ?? "",
                    email: roleRow.contact.email ?? "",
                    roleLabels: [roleLabel],
                });
            }
        }
        return Array.from(byContactId.values());
    }

    private getContactForRole(targetRoleId: number): { contactId: number; name?: string } | undefined {
        const roleArray: any[] = S25Util.array.forceArray(this.eventResource.value()?.role ?? []);
        const match = roleArray.find((obj) => obj?.contact?.contact_id && parseInt(obj.role_id) === targetRoleId);
        if (!match) return undefined;
        return {
            contactId: parseInt(match.contact.contact_id),
            name: match.contact.contact_name,
        };
    }

    private async resolveContacts(
        roleEntries: { name?: string; email?: string; contactId?: number }[],
    ): Promise<SocContactUser[]> {
        const valid = roleEntries.filter((entry) => entry?.contactId);
        if (!valid.length) return [];

        const uniqueContactIds = Array.from(new Set(valid.map((entry) => entry.contactId!)));
        const needsAddressInclude = valid.some((entry) => !entry.email);
        const includes = needsAddressInclude ? ["user", "address"] : ["user"];
        const data = await ContactService.getFilterContacts(uniqueContactIds, includes);
        const contactNodes = S25Util.array.forceArray(data?.contacts?.contact ?? []) as ContactWsNode[];
        const contactById = new Map<number, ContactWsNode>(
            contactNodes
                .filter((node) => node?.contact_id != null)
                .map((node) => [Number(node.contact_id), node] as const),
        );

        return valid
            .map((entry) => {
                const contactNode = contactById.get(entry.contactId!);
                const username = S25Util.propertyGetVal(contactNode, "r25_username");
                if (!username) return null;
                const [fallbackLastname = "", fallbackFirstname = ""] = (entry.name ?? "").split(", ");
                const firstname = contactNode?.first_name || fallbackFirstname;
                const lastname = contactNode?.last_name || fallbackLastname;
                const email = entry.email || pickPrimaryEmailFromAddress(contactNode) || "";
                return {
                    userType: "25live",
                    contId: entry.contactId!,
                    username,
                    firstname,
                    lastname,
                    email,
                } satisfies SocContactUser;
            })
            .filter((user): user is SocContactUser => user != null);
    }

    private notifySkippedUsers(result: SocPublicChannelResult) {
        if (!result.skippedUsers?.length) return;
        const items = result.skippedUsers
            .map((user) => `<li>${user.username ?? user.contId} — ${user.reason}</li>`)
            .join("");
        NotificationService.post(
            `Some users could not be added to the chat.<details style="margin-top:6px"><summary style="cursor:pointer">${result.skippedUsers.length} skipped user(s) — click for details</summary><ul style="margin-top:4px; padding-left:1.5em">${items}</ul></details>`,
        );
    }
}

function pickPrimaryEmailFromAddress(contactNode: ContactWsNode | undefined): string | undefined {
    const addresses = S25Util.array.forceArray(contactNode?.address ?? []) as ContactWsAddress[];
    return addresses.map((address) => address?.email).find((email): email is string => !!email);
}
